import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { IconButton } from "@mui/material";
import axios from "../../utils/api.js";

const HospitalForm = () => {
  const [formData, setFormData] = useState({
    basicInfo: {
      hospitalName: "",
      hospitalType: "",
      hospitalSize: "",
      totalFloorArea: 0,
      totalFloors: 0,
      totalDepartments: 0,
      totalBeds: 0,
      accreditationCertifications: "",
    },
    dataCollection: {
      dateOfDataCollection: "",
      dataCollectedBy: "",
      dataSource: "",
    },
    infraPatientFlow: {
      noOfbedroomsOrWards: 0,
      normalRoomCostPerDay: 0,
      normalRoomOccupancyRate: 0,
      acRoomCostPerDay: 0,
      acRoomOccupancyRate: 0,
      icuWardCount: 0,
      icuCostPerDay: 0,
      icuWardOccupancyRate: 0,
      operationTheaters: 0,
      otOccupancyRate: 0,
      mriCtScanRooms: 0,
      mriCtScanUtilizationRate: 0,
      outpatientConsultationRooms: 0,
      outpatientAreaOccupancyPerDay: 0,
      receptionWaitingFootfallPerDay: 0,
    },
    hygieneDisinfection: {
      totalCleaningBudgetPerMonth: 0,
      cleaningCostsbedroomsWards: 0,
      cleaningCostsicuUnits: 0,
      cleaningCostsoperationTheaters: 0,
      cleaningCostsmriScanningUnits: 0,
      cleaningCostsoutpatientAreas: 0,
      cleaningCostspharmacyAreas: 0,
      cleaningCostsreceptionAreas: 0,
      cleaningCostscanteensKitchen: 0,
      currentDisinfectionTechniques: "",
      avgTimePerDisinfection: 0,
      disinfectionStaffAvailability: "",
      challengesinCurrentCleaningandDisinfection: "",
    },
    infectionControl: {
      hasInfectionControlTeam: false,
      standardProtocolsFollowed: "",
      auditFrequency: "",
      pastNosocomialIncidents: 0,
      recentHygieneComplaints: "",
    },
    staffExposure: {
      avgTimeSpentCleaning: 0,
      disinfectionComplaints: "",
      reportsChemicalExposure: "",
      currentPpeUsage: "",
      automationReadiness: false,
    },
    timeCostAnalysis: {
      avgManualDisinfectionTimePerDay: 0,
      highRiskAreaDisinfectionTime: 0,
      chemicalDisinfectionDryTime: 0,
      laborCostPerMonth: 0,
      disinfectionChemicalCostPerMonth: 0,
      electricityConsumption: 0,
      infectionRelatedComplicationCost: 0,
    },
    additionalnotes: {
      note: "",
    },
  });
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);

  const FORM_STEPS = [
    "Basic Info",
    "Data Collection",
    "Infrastructure & Patient Flow",
    "Hygiene & Disinfection",
    "Infection Control",
    "Staff Exposure",
    "Time & Cost Analysis",
    "Additional Notes",
  ];
  const totalSteps = FORM_STEPS.length;

  const pageStyle = {
    position: "relative",
    minHeight: "100vh",
    background:
      "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    padding: "24px 32px 48px",
    boxSizing: "border-box",
  };

  const cardStyle = {
    maxWidth: 720,
    margin: "0 auto 24px",
    backgroundColor: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 14,
    padding: 28,
    boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
  };
  const inputGroupStyle = { marginBottom: 18 };
  const labelStyle = {
    display: "block",
    marginBottom: 6,
    fontWeight: 600,
    fontSize: "0.9rem",
    color: "#64748b",
  };
  const inputStyle = {
    width: "100%",
    padding: "0.65rem 0.85rem",
    borderRadius: 8,
    border: "1px solid #e2e8f0",
    fontSize: "1rem",
    outline: "none",
    boxSizing: "border-box",
  };
  const sectionTitleStyle = {
    margin: "0 0 20px",
    fontSize: "1.1rem",
    fontWeight: 600,
    color: "#1e293b",
  };

  const handleChange = (e, section, nestedSection) => {
    const { name, value, type } = e.target;
    let newValue = value;

    if (type === "number") {
      newValue = Math.max(0, Number(value)) || 0;
    } else if (type === "checkbox") {
      newValue = e.target.checked; // Convert to boolean
    }

    if (nestedSection) {
      setFormData((prevData) => ({
        ...prevData,
        [section]: {
          ...prevData[section],
          [nestedSection]: {
            ...prevData[section][nestedSection],
            [name]: newValue,
          },
        },
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [section]: {
          ...prevData[section],
          [name]: newValue,
        },
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");

      const response = await axios.post("/post-hospitals", formData, {
        headers: {
          Authorization: `Bearer ${token}`, // Add auth token
          "Content-Type": "application/json",
        },
      });

      console.log("Data posted successfully:", response.data);
      alert("Hospital data submitted successfully!");
      navigate("/Viewhospital");
      // Reset form after successful submission
      setFormData({
        basicInfo: {
          hospitalName: "",
          hospitalType: "",
          hospitalSize: "",
          totalFloorArea: 0,
          totalFloors: 0,
          totalDepartments: 0,
          totalBeds: 0,
          accreditationCertifications: "",
        },
        dataCollection: {
          dateOfDataCollection: "",
          dataCollectedBy: "",
          dataSource: "",
        },
        infraPatientFlow: {
          noOfbedroomsOrWards: 0,
          normalRoomCostPerDay: 0,
          normalRoomOccupancyRate: 0,
          acRoomCostPerDay: 0,
          acRoomOccupancyRate: 0,
          icuWardCount: 0,
          icuCostPerDay: 0,
          icuWardOccupancyRate: 0,
          operationTheaters: 0,
          otOccupancyRate: 0,
          mriCtScanRooms: 0,
          mriCtScanUtilizationRate: 0,
          outpatientConsultationRooms: 0,
          outpatientAreaOccupancyPerDay: 0,
          receptionWaitingFootfallPerDay: 0,
        },
        hygieneDisinfection: {
          totalCleaningBudgetPerMonth: 0,
          cleaningCostsbedroomsWards: 0,
          cleaningCostsicuUnits: 0,
          cleaningCostsoperationTheaters: 0,
          cleaningCostsmriScanningUnits: 0,
          cleaningCostsoutpatientAreas: 0,
          cleaningCostspharmacyAreas: 0,
          cleaningCostsreceptionAreas: 0,
          cleaningCostscanteensKitchen: 0,
          currentDisinfectionTechniques: "",
          avgTimePerDisinfection: 0,
          disinfectionStaffAvailability: "",
          challengesinCurrentCleaningandDisinfection: "",
        },
        infectionControl: {
          hasInfectionControlTeam: false,
          standardProtocolsFollowed: "",
          auditFrequency: "",
          pastNosocomialIncidents: 0,
          recentHygieneComplaints: "",
        },
        staffExposure: {
          avgTimeSpentCleaning: 0,
          disinfectionComplaints: "",
          reportsChemicalExposure: "",
          currentPpeUsage: "",
          automationReadiness: false,
        },
        timeCostAnalysis: {
          avgManualDisinfectionTimePerDay: 0,
          highRiskAreaDisinfectionTime: 0,
          chemicalDisinfectionDryTime: 0,
          laborCostPerMonth: 0,
          disinfectionChemicalCostPerMonth: 0,
          electricityConsumption: 0,
          infectionRelatedComplicationCost: 0,
        },
        additionalnotes: {
          note: "",
        },
      });
    } catch (error) {
      console.error(
        "Error posting data:",
        error.response?.data || error.message
      );
      alert("Failed to submit hospital data.");
    }
  };

  return (
    <div style={pageStyle}>
      <IconButton
        onClick={() => navigate(-1)}
        style={{
          position: "absolute",
          top: 24,
          left: 24,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(10px)",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          color: "#4a4a4a",
          zIndex: 10,
        }}
      >
        <ArrowBackIcon />
      </IconButton>

      <div style={{ textAlign: "center", marginTop: 20, marginBottom: 28 }}>
        <h1
          style={{
            margin: 0,
            marginBottom: 8,
            fontSize: "1.85rem",
            fontWeight: 700,
            letterSpacing: "-0.5px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          Hospital Form
        </h1>
        <p
          style={{
            margin: 0,
            fontSize: "0.95rem",
            color: "#64748b",
            fontWeight: 400,
          }}
        >
          Complete the sections below. Use Previous and Next to move between steps.
        </p>
      </div>

      {/* FORM_STEPS: centered strip above card */}
      <div
        style={{
          maxWidth: 720,
          margin: "0 auto 20px",
          display: "flex",
          justifyContent: "center",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        {FORM_STEPS.map((name, i) => (
          <span
            key={i}
            style={{
              padding: "6px 12px",
              borderRadius: 8,
              fontSize: "0.8rem",
              fontWeight: 500,
              background:
                currentStep === i
                  ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                  : "#e2e8f0",
              color: currentStep === i ? "#fff" : "#64748b",
            }}
          >
            {name}
          </span>
        ))}
      </div>

      <form onSubmit={handleSubmit}>
        <div style={cardStyle}>
          {/* Step: Basic Info */}
          {currentStep === 0 && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 20,
                }}
              >
                <h3 style={sectionTitleStyle}>Basic Info</h3>
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.8rem",
                    color: "#64748b",
                    fontWeight: 500,
                  }}
                >
                  Step 1 of {totalSteps}
                </p>
              </div>
              {[
            { label: "Hospital Name", name: "hospitalName", type: "text" },
            { label: "Hospital Type", name: "hospitalType", type: "text" },
            { label: "Hospital Size", name: "hospitalSize", type: "text" },
            {
              label: "Total Floor Area",
              name: "totalFloorArea",
              type: "number",
            },
            { label: "Total Floors", name: "totalFloors", type: "number" },
            {
              label: "Total Departments",
              name: "totalDepartments",
              type: "number",
            },
            { label: "Total Beds", name: "totalBeds", type: "number" },
            {
              label: "Accreditations",
              name: "accreditationCertifications",
              type: "text",
            },
              ].map(({ label, name, type }) => (
                <div style={inputGroupStyle} key={name}>
                  <label style={labelStyle}>{label}</label>
                  <input
                    type={type}
                    name={name}
                    placeholder={
                      type === "number"
                        ? "Enter a number"
                        : "Enter " + label.toLowerCase()
                    }
                    value={formData.basicInfo[name] || ""}
                    onChange={(e) => {
                      const val =
                        type === "number"
                          ? Math.max(0, Number(e.target.value)) || 0
                          : e.target.value;
                      handleChange(
                        { target: { name, value: val } },
                        "basicInfo"
                      );
                    }}
                    style={inputStyle}
                  />
                </div>
              ))}
            </>
          )}

          {/* Step: Data Collection */}
          {currentStep === 1 && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 20,
                }}
              >
                <h3 style={sectionTitleStyle}>Data Collection</h3>
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.8rem",
                    color: "#64748b",
                    fontWeight: 500,
                  }}
                >
                  Step 2 of {totalSteps}
                </p>
              </div>
              {[
            {
              label: "Date of Data Collection",
              name: "dateOfDataCollection",
              type: "date",
            },
            {
              label: "Data Collected By",
              name: "dataCollectedBy",
              type: "text",
            },
                {
                  label: "Data Source:",
                  name: "dataSource",
                  type: "text",
                },
              ].map(({ label, name, type }) => (
                <div style={inputGroupStyle} key={name}>
                  <label style={labelStyle}>{label}</label>
                  <input
                    type={type}
                    name={name}
                    placeholder={
                      type === "date" ? "Select a date" : "Enter text"
                    }
                    value={formData.dataCollection[name] || ""}
                    onChange={(e) => handleChange(e, "dataCollection")}
                    style={inputStyle}
                  />
                </div>
              ))}
            </>
          )}

          {/* Step: Infrastructure & Patient Flow */}
          {currentStep === 2 && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 20,
                }}
              >
                <h3 style={sectionTitleStyle}>
                  Infrastructure & Patient Flow
                </h3>
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.8rem",
                    color: "#64748b",
                    fontWeight: 500,
                  }}
                >
                  Step 3 of {totalSteps}
                </p>
              </div>
              {[
            {
              label: "No. of Bedrooms/Wards",
              name: "noOfbedroomsOrWards",
              type: "number",
            },
            {
              label: "Normal Room Cost Per Day",
              name: "normalRoomCostPerDay",
              type: "number",
            },
            {
              label: "Normal Room Occupancy Rate",
              name: "normalRoomOccupancyRate",
              type: "number",
            },
            {
              label: "AC Room Cost Per Day",
              name: "acRoomCostPerDay",
              type: "number",
            },
            {
              label: "AC Room Occupancy Rate",
              name: "acRoomOccupancyRate",
              type: "number",
            },
            { label: "ICU Ward Count", name: "icuWardCount", type: "number" },
            {
              label: "ICU Cost Per Day",
              name: "icuCostPerDay",
              type: "number",
            },
            {
              label: "ICU Ward Occupancy Rate",
              name: "icuWardOccupancyRate",
              type: "number",
            },
            {
              label: "Operation Theaters",
              name: "operationTheaters",
              type: "number",
            },
            {
              label: "OT Occupancy Rate",
              name: "otOccupancyRate",
              type: "number",
            },
            {
              label: "MRI/CT Scan Rooms",
              name: "mriCtScanRooms",
              type: "number",
            },
            {
              label: "MRI/CT Scan Utilization Rate",
              name: "mriCtScanUtilizationRate",
              type: "number",
            },
            {
              label: "Outpatient Consultation Rooms",
              name: "outpatientConsultationRooms",
              type: "number",
            },
            {
              label: "Outpatient Area Occupancy Per Day",
              name: "outpatientAreaOccupancyPerDay",
              type: "number",
            },
                {
                  label: "Reception Waiting Footfall Per Day",
                  name: "receptionWaitingFootfallPerDay",
                  type: "number",
                },
              ].map(({ label, name, type }) => (
                <div style={inputGroupStyle} key={name}>
                  <label style={labelStyle}>{label}</label>
                  <input
                    type={type}
                    name={name}
                    placeholder={`Enter ${type === "number" ? "a number" : "text"}`}
                    value={formData.infraPatientFlow[name] || ""}
                    onChange={(e) => {
                      const val =
                        type === "number"
                          ? Math.max(0, Number(e.target.value)) || 0
                          : e.target.value;
                      handleChange(
                        { target: { name, value: val } },
                        "infraPatientFlow"
                      );
                    }}
                    style={inputStyle}
                  />
                </div>
              ))}
            </>
          )}

          {/* Step: Hygiene & Disinfection */}
          {currentStep === 3 && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 20,
                }}
              >
                <h3 style={sectionTitleStyle}>Hygiene & Disinfection</h3>
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.8rem",
                    color: "#64748b",
                    fontWeight: 500,
                  }}
                >
                  Step 4 of {totalSteps}
                </p>
              </div>
              {[
            {
              label: "Total Cleaning Budget Per Month",
              name: "totalCleaningBudgetPerMonth",
              type: "number",
            },
            {
              label: "Cleaning Costs - Bedrooms/Wards",
              name: "cleaningCostsbedroomsWards",
              type: "number",
            },
            {
              label: "Cleaning Costs - ICU Units",
              name: "cleaningCostsicuUnits",
              type: "number",
            },
            {
              label: "Cleaning Costs - Operation Theaters",
              name: "cleaningCostsoperationTheaters",
              type: "number",
            },
            {
              label: "Cleaning Costs - MRI Scanning Units",
              name: "cleaningCostsmriScanningUnits",
              type: "number",
            },
            {
              label: "Cleaning Costs - Outpatient Areas",
              name: "cleaningCostsoutpatientAreas",
              type: "number",
            },
            {
              label: "Cleaning Costs - Pharmacy Areas",
              name: "cleaningCostspharmacyAreas",
              type: "number",
            },
            {
              label: "Cleaning Costs - Reception Areas",
              name: "cleaningCostsreceptionAreas",
              type: "number",
            },
            {
              label: "Cleaning Costs - Canteens/Kitchen",
              name: "cleaningCostscanteensKitchen",
              type: "number",
            },
            {
              label: "Current Disinfection Techniques",
              name: "currentDisinfectionTechniques",
              type: "text",
            },
            {
              label: "Average Time Per Disinfection (min)",
              name: "avgTimePerDisinfection",
              type: "number",
            },
            {
              label: "Disinfection Staff Availability",
              name: "disinfectionStaffAvailability",
              type: "text",
            },
                {
                  label: "Challenges in Cleaning & Disinfection",
                  name: "challengesinCurrentCleaningandDisinfection",
                  type: "text",
                },
              ].map(({ label, name, type }) => (
                <div style={inputGroupStyle} key={name}>
                  <label style={labelStyle}>{label}</label>
                  <input
                    type={type}
                    name={name}
                    placeholder={`Enter ${type === "number" ? "number" : "text"}`}
                    value={
                      Array.isArray(formData.hygieneDisinfection[name])
                        ? formData.hygieneDisinfection[name].join(", ")
                        : formData.hygieneDisinfection[name] || ""
                    }
                    onChange={(e) => {
                      let value = e.target.value;
                      if (type === "number") {
                        value = Math.max(0, Number(value)) || 0;
                      }
                      if (Array.isArray(formData.hygieneDisinfection[name]))
                        value = value.split(",").map((item) => item.trim());

                      handleChange(
                        { target: { name, value } },
                        "hygieneDisinfection"
                      );
                    }}
                    style={inputStyle}
                  />
                </div>
              ))}
            </>
          )}

          {/* Step: Infection Control */}
          {currentStep === 4 && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 20,
                }}
              >
                <h3 style={sectionTitleStyle}>Infection Control</h3>
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.8rem",
                    color: "#64748b",
                    fontWeight: 500,
                  }}
                >
                  Step 5 of {totalSteps}
                </p>
              </div>
              {[
            {
              label: "Has Infection Control Team",
              name: "hasInfectionControlTeam",
              type: "radio",
            },
            {
              label: "Standard Protocols Followed",
              name: "standardProtocolsFollowed",
              type: "text",
            },
            { label: "Audit Frequency", name: "auditFrequency", type: "text" },
            {
              label: "Past Nosocomial Incidents - (HAIs)",
              name: "pastNosocomialIncidents",
              type: "number",
            },
                {
                  label: "Recent Hygiene Complaints",
                  name: "recentHygieneComplaints",
                  type: "text",
                },
              ].map(({ label, name, type }) => (
                <div style={inputGroupStyle} key={name}>
                  <label style={labelStyle}>{label}</label>

                  {type === "radio" ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "20px",
                      }}
                    >
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <input
                          type="radio"
                          name={name}
                          value="true"
                          checked={formData.infectionControl[name] === true}
                          onChange={() =>
                            handleChange(
                              { target: { name, value: true } },
                              "infectionControl"
                            )
                          }
                          style={{
                            width: "18px",
                            height: "18px",
                            cursor: "pointer",
                          }}
                        />
                        <span style={{ fontWeight: 600, color: "#059669" }}>
                          True
                        </span>
                      </label>
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <input
                          type="radio"
                          name={name}
                          value="false"
                          checked={formData.infectionControl[name] === false}
                          onChange={() =>
                            handleChange(
                              { target: { name, value: false } },
                              "infectionControl"
                            )
                          }
                          style={{
                            width: "18px",
                            height: "18px",
                            cursor: "pointer",
                          }}
                        />
                        <span style={{ fontWeight: 600, color: "#dc2626" }}>
                          False
                        </span>
                      </label>
                    </div>
                  ) : (
                    <input
                      type={type}
                      name={name}
                      placeholder={
                        type === "number"
                          ? "Enter number"
                          : "Enter " + label.toLowerCase()
                      }
                      value={formData.infectionControl[name] || ""}
                      onChange={(e) => {
                        const val =
                          type === "number"
                            ? Math.max(0, Number(e.target.value)) || 0
                            : e.target.value;
                        handleChange(
                          { target: { name, value: val } },
                          "infectionControl"
                        );
                      }}
                      style={inputStyle}
                    />
                  )}
                </div>
              ))}
            </>
          )}

          {/* Step: Staff Exposure */}
          {currentStep === 5 && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 20,
                }}
              >
                <h3 style={sectionTitleStyle}>Staff Exposure</h3>
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.8rem",
                    color: "#64748b",
                    fontWeight: 500,
                  }}
                >
                  Step 6 of {totalSteps}
                </p>
              </div>
              {[
            {
              label: "Avg. Time Spent Cleaning (mins)",
              name: "avgTimeSpentCleaning",
              type: "number",
            },
            {
              label: "Disinfection Complaints",
              name: "disinfectionComplaints",
              type: "text",
            },
            {
              label: "Reports of Chemical Exposure",
              name: "reportsChemicalExposure",
              type: "text",
            },
            {
              label: "Current PPE Usage",
              name: "currentPpeUsage",
              type: "text",
            },
                {
                  label: "Automation Readiness",
                  name: "automationReadiness",
                  type: "radio",
                },
              ].map(({ label, name, type }) => (
                <div style={inputGroupStyle} key={name}>
                  <label style={labelStyle}>{label}</label>

                  {type === "radio" ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "20px",
                      }}
                    >
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <input
                          type="radio"
                          name={name}
                          value="true"
                          checked={formData.staffExposure[name] === true}
                          onChange={() =>
                            handleChange(
                              { target: { name, value: true } },
                              "staffExposure"
                            )
                          }
                          style={{
                            width: "18px",
                            height: "18px",
                            cursor: "pointer",
                          }}
                        />
                        <span style={{ fontWeight: 600, color: "#059669" }}>
                          True
                        </span>
                      </label>
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <input
                          type="radio"
                          name={name}
                          value="false"
                          checked={formData.staffExposure[name] === false}
                          onChange={() =>
                            handleChange(
                              { target: { name, value: false } },
                              "staffExposure"
                            )
                          }
                          style={{
                            width: "18px",
                            height: "18px",
                            cursor: "pointer",
                          }}
                        />
                        <span style={{ fontWeight: 600, color: "#dc2626" }}>
                          False
                        </span>
                      </label>
                    </div>
                  ) : (
                    <input
                      type={type}
                      name={name}
                      placeholder={
                        type === "number"
                          ? "Enter number"
                          : "Enter " + label.toLowerCase()
                      }
                      value={formData.staffExposure[name] || ""}
                      onChange={(e) => {
                        const val =
                          type === "number"
                            ? Math.max(0, Number(e.target.value)) || 0
                            : e.target.value;
                        handleChange(
                          { target: { name, value: val } },
                          "staffExposure"
                        );
                      }}
                      style={inputStyle}
                    />
                  )}
                </div>
              ))}
            </>
          )}

          {/* Step: Time & Cost Analysis */}
          {currentStep === 6 && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 20,
                }}
              >
                <h3 style={sectionTitleStyle}>Time & Cost Analysis</h3>
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.8rem",
                    color: "#64748b",
                    fontWeight: 500,
                  }}
                >
                  Step 7 of {totalSteps}
                </p>
              </div>
              {[
            {
              label: "Avg. Manual Disinfection Time Per Day (mins)",
              name: "avgManualDisinfectionTimePerDay",
              type: "number",
            },
            {
              label: "High-Risk Area Disinfection Time (mins)",
              name: "highRiskAreaDisinfectionTime",
              type: "number",
            },
            {
              label: "Chemical Disinfection Dry Time (mins)",
              name: "chemicalDisinfectionDryTime",
              type: "number",
            },
            {
              label: "Labor Cost Per Month ($)",
              name: "laborCostPerMonth",
              type: "number",
            },
            {
              label: "Disinfection Chemical Cost Per Month ($)",
              name: "disinfectionChemicalCostPerMonth",
              type: "number",
            },
            {
              label: "Electricity Consumption (kWh)",
              name: "electricityConsumption",
              type: "number",
            },
                {
                  label: "Infection-Related Complication Cost ($)",
                  name: "infectionRelatedComplicationCost",
                  type: "number",
                },
              ].map(({ label, name, type }) => (
                <div style={inputGroupStyle} key={name}>
                  <label style={labelStyle}>{label}</label>
                  <input
                    type={type}
                    name={name}
                    placeholder={
                      type === "number"
                        ? "Enter a number"
                        : "Enter " + label.toLowerCase()
                    }
                    value={formData.timeCostAnalysis[name] || ""}
                    onChange={(e) => {
                      const val =
                        type === "number"
                          ? Math.max(0, Number(e.target.value)) || 0
                          : e.target.value;
                      handleChange(
                        { target: { name, value: val } },
                        "timeCostAnalysis"
                      );
                    }}
                    style={inputStyle}
                  />
                </div>
              ))}
            </>
          )}

          {/* Step: Additional Notes */}
          {currentStep === 7 && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 20,
                }}
              >
                <h3 style={sectionTitleStyle}>Additional Notes</h3>
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.8rem",
                    color: "#64748b",
                    fontWeight: 500,
                  }}
                >
                  Step 8 of {totalSteps}
                </p>
              </div>
              <div style={inputGroupStyle}>
                <label style={labelStyle}>Notes</label>
                <textarea
                  name="note"
                  value={formData.additionalnotes.note}
                  onChange={(e) => handleChange(e, "additionalnotes")}
                  style={{
                    width: "100%",
                    minHeight: 120,
                    padding: "0.65rem 0.85rem",
                    borderRadius: 8,
                    border: "1px solid #e2e8f0",
                    fontSize: "1rem",
                    outline: "none",
                    boxSizing: "border-box",
                    resize: "vertical",
                  }}
                  placeholder="Enter any additional notes here..."
                />
              </div>
            </>
          )}
        </div>

        {/* Step navigation: Previous / Next or Submit */}
        <div
          style={{
            maxWidth: 720,
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent:
              currentStep === 0 ? "flex-end" : "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          {currentStep > 0 ? (
            <button
              type="button"
              onClick={() => setCurrentStep((s) => s - 1)}
              style={{
                padding: "0.65rem 1.25rem",
                fontSize: "1rem",
                fontWeight: 600,
                color: "#64748b",
                background: "#fff",
                border: "1px solid #e2e8f0",
                borderRadius: 8,
                cursor: "pointer",
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
              }}
            >
              Previous
            </button>
          ) : (
            <span />
          )}
          {currentStep < totalSteps - 1 ? (
            <button
              type="button"
              onClick={() => setCurrentStep((s) => s + 1)}
              style={{
                padding: "0.65rem 1.25rem",
                fontSize: "1rem",
                fontWeight: 600,
                background:
                  "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                cursor: "pointer",
                boxShadow: "0 2px 8px rgba(102, 126, 234, 0.3)",
              }}
            >
              Next
            </button>
          ) : (
            <button
              type="submit"
              style={{
                padding: "0.65rem 1.25rem",
                fontSize: "1rem",
                fontWeight: 600,
                background:
                  "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                cursor: "pointer",
                boxShadow: "0 2px 8px rgba(102, 126, 234, 0.3)",
              }}
            >
              Submit
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default HospitalForm;
